import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import axios from "axios";
import { isEqual } from "lodash";
import { formatPhoneNumber } from "../../utils/Helpers";
import { useQueryParams } from "../../hoc/navigation/withParams";

interface InstallerLoginProps {
    onLoginSuccess: () => void;
    onLoginError: () => void;
}

export const InstallerLogin = React.memo(({ onLoginSuccess, onLoginError }: InstallerLoginProps) => {
    const hasAttemptedAuth = useRef(false);

    const { jobId } = useParams();
    const { getParam } = useQueryParams();
    const workerPhoneNumber = getParam("workerPhoneNumber");

    useEffect(() => {
        // Prevent multiple authentication attempts
        if (hasAttemptedAuth.current) {
            return;
        }
        hasAttemptedAuth.current = true;

        const performAuthentication = async () => {
            // Check if already authenticated
            const authToken = localStorage.getItem("accessToken");
            const role = localStorage.getItem("userRole");

            // If authenticated with wrong role, call error callback
            if (authToken && role !== "installer") {
                onLoginError();
                return;
            }

            // If already authenticated as installer, call success callback
            if (authToken && role === "installer") {
                onLoginSuccess();
                return;
            }

            // If not authenticated and no phone number, wait for manual login
            if (!workerPhoneNumber) {
                // No auto-login possible, component stays on loading screen
                // In a future enhancement, you could show a manual login form here
                return;
            }

            // Auto-login with phone number
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_URL}/token/temp/installer/${jobId}`,
                    new URLSearchParams({
                        grant_type: "password",
                        username: formatPhoneNumber(workerPhoneNumber),
                        password: formatPhoneNumber(workerPhoneNumber),
                        scope: "",
                        client_id: "",
                        client_secret: "",
                    }),
                    {
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded",
                            Accept: "application/json",
                        },
                    },
                );
                const accessToken = response.data.access_token;
                const userRole = response.data.role;

                // Store token in localStorage
                localStorage.setItem("accessToken", accessToken);
                localStorage.setItem("userRole", userRole);

                onLoginSuccess();
            } catch (err) {
                console.error("Auto-login failed:", err);
                onLoginError();
            }
        };

        performAuthentication();
        // Only run once on mount - callbacks are intentionally NOT in deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId, workerPhoneNumber]);

    return (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
            <CircularProgress />
        </div>
    );
}, isEqual);
